import axios, { AxiosRequestConfig } from "axios";
import HTTPSCalls from "../Services/HTTPCalls";
import { AuthService } from "../Services/AuthService";

const getBaseUrl = () => {
  return process.env.REACT_APP_BASE_URL?.slice(0, -4);
};

export const axiosInstance = axios.create({
  baseURL: getBaseUrl(),
  withCredentials: true,
});

// ---- token refresh queue ----
let isRefreshing = false;
let failedQueue: {
  resolve: (value?: any) => void;
  reject: (err: any) => void;
  originalRequest: AxiosRequestConfig;
}[] = [];

const processQueue = (error: any = null) => {
  failedQueue.forEach(({ resolve, reject, originalRequest }) => {
    if (error) reject(error);
    else resolve(axiosInstance(originalRequest));
  });
  failedQueue = [];
};

// ---- interceptor ----
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Don't retry certain endpoints
    const url = originalRequest.url?.toLowerCase() || "";
    const isBlacklisted =
      url.includes("/login") ||
      url.includes("/logout") ||
      url.includes("/refresh") ||
      url.includes("/verify2fa");

    if (isBlacklisted) return Promise.reject(error);

    // ---- Handle 401 ----
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // If another request is already refreshing
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject, originalRequest });
        });
      }

      isRefreshing = true;

      try {
        const res = await HTTPSCalls.POST(
          HTTPSCalls.ENDPOINTS.REFRESH,
          null,
          {}
        );

        if (res?.status) {
          processQueue(null); // retry queued requests
          return axiosInstance(originalRequest); // retry current one
        } else {
          throw new Error("Refresh token expired");
        }
      } catch (refreshErr) {
        processQueue(refreshErr);
        AuthService.logout();
        throw refreshErr;
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// ✅ Custom Axios Mutator wrapper for Orval or generic use
export const customAxios = <T>(config: AxiosRequestConfig): Promise<T> => {
  return axiosInstance.request<T>(config).then((res) => res.data);
};
